import type { GetStaticPaths, GetStaticPropsContext } from "next";

import type { ArtistType, PostType } from "@sorcery/cms";

import { getSecret } from "~/server/sanity/preview";
import {
  cdnCacheOptOutClient,
  GET_ARTIST_BY_SLUG,
  GET_ARTIST_POSTS,
  GET_ARTIST_SLUGS,
  PREVIEW_GET_ARTIST_BY_SLUG,
} from "~/server/sanity/query";
import { env } from "~/env";

export const getStaticProps = async (context: GetStaticPropsContext<{ slug: string }>) => {
  const slug = context.params?.slug;
  const { preview = false, previewData = {} } = context;
  if (preview) {
    const providedSecret = (previewData as { secret?: string }).secret;
    const secret = await getSecret(cdnCacheOptOutClient, "preview.secret");
    if (providedSecret !== secret) {
      console.error("⚡ Sorcery artist preview mode secret mismatch / rotate");
      throw new Error("Preview mode secret mismatch");
    }
  }

  const data = await cdnCacheOptOutClient.fetch<ArtistType>(
    preview ? PREVIEW_GET_ARTIST_BY_SLUG : GET_ARTIST_BY_SLUG,
    { slug },
  );

  const features = await cdnCacheOptOutClient.fetch<PostType[]>(GET_ARTIST_POSTS, {
    slug: slug,
  });

  return {
    props: {
      data,
      slug,
      preview,
      features,
    },
  };
};

export const getStaticPaths = (async () => {
  // don't prerender any static pages
  // (faster builds, but slower initial page load)
  if (env.VERCEL_ENV === "preview") {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
  // Populate potential dynamic query slugs
  const slugs = await cdnCacheOptOutClient.fetch<string[]>(GET_ARTIST_SLUGS);

  // Get the paths we want to prerender based on our ids
  const paths = slugs.map((slug) => ({
    params: { slug },
  }));

  // { fallback: 'blocking' } means other routes not cached at build time should cause execution to wait while they build
  return { paths, fallback: "blocking" };
}) satisfies GetStaticPaths;

export { default } from "./Artist";
